<template>
  <v-dialog v-model="open" persistent max-width="820" scrollable>
    <v-card class="re-enrollment-dialog">
      <v-card-title>
        <div><div class="overline primary--text">Parent enrollment</div><div>Re-enroll students</div></div>
        <v-spacer /><v-btn icon :disabled="saving" @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-0">
        <v-skeleton-loader v-if="loading" type="list-item-three-line, list-item-three-line" class="pa-6" />
        <v-stepper v-else v-model="step" flat>
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1">Students</v-stepper-step><v-divider />
            <v-stepper-step :complete="step > 2" step="2">Departments</v-stepper-step><v-divider />
            <v-stepper-step step="3">Review</v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <p class="grey--text text--darken-1">Select every former student returning to the institute.</p>
              <v-alert v-if="!students.length" text type="info">This parent has no former students to re-enroll.</v-alert>
              <v-checkbox v-for="student in students" :key="student.id" v-model="selectedStudentIds"
                :value="student.id" hide-details class="re-enrollment-dialog__student">
                <template #label><span><strong>{{ student.fullName }}</strong><small>Left {{ student.leftDate | moment("MMM D, YYYY") }}</small></span></template>
              </v-checkbox>
            </v-stepper-content>
            <v-stepper-content step="2">
              <v-text-field v-model="effectiveDate" type="date" label="Return date" outlined dense hide-details class="mb-5" />
              <div v-for="selection in selections" :key="selection.studentId" class="re-enrollment-dialog__assignment">
                <strong>{{ studentName(selection.studentId) }}</strong>
                <v-select v-model="selection.departmentIds" :items="departments" item-text="name" item-value="id"
                  label="Departments" multiple chips small-chips outlined dense hide-details class="mt-2" />
                <small v-if="previousDepartmentNames(selection.studentId)">Previous: {{ previousDepartmentNames(selection.studentId) }}</small>
              </div>
            </v-stepper-content>
            <v-stepper-content step="3">
              <v-alert v-if="preview && preview.needsProration" text color="primary" icon="mdi-calendar-clock">
                The regular invoice window has passed. One combined prorated invoice will be created now and will be due two days later.
              </v-alert>
              <v-alert v-else text type="info">No immediate invoice is needed. These students will enter the normal monthly billing cycle.</v-alert>
              <div v-if="preview && preview.lines" class="re-enrollment-dialog__review">
                <div v-for="line in preview.lines" :key="`${line.studentId}-${line.departmentId}`">
                  <span><strong>{{ line.studentName }}</strong><small>{{ line.departmentName }} · {{ line.remainingDays }} of {{ line.daysInMonth }} days</small></span>
                  <strong>{{ line.proratedAmount | currency }}</strong>
                </div>
                <div class="re-enrollment-dialog__total"><span>Prorated invoice total</span><strong>{{ preview.total | currency }}</strong></div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn v-if="step > 1" text :disabled="saving" @click="step -= 1">Back</v-btn>
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn v-if="step < 3" color="primary" depressed :disabled="!canContinue" :loading="previewing" @click="next">Next</v-btn>
        <v-btn v-else color="primary" depressed :loading="saving" @click="submit">Complete re-enrollment</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import customerService from "../service";

export default {
  name: "ReEnrollment",
  props: { value: Boolean, customerId: { type: Number, required: true } },
  data: () => ({ loading: false, previewing: false, saving: false, step: 1, students: [], departments: [], selectedStudentIds: [], selections: [], effectiveDate: "", preview: null }),
  computed: {
    open: { get() { return this.value; }, set(value) { this.$emit("input", value); } },
    canContinue() {
      if (this.step === 1) return this.selectedStudentIds.length > 0;
      return Boolean(this.effectiveDate) && this.selections.length > 0 && this.selections.every(item => item.departmentIds.length > 0);
    },
    payload() { return { effectiveDate: this.effectiveDate, students: this.selections.map(item => ({ studentId: item.studentId, departmentIds: item.departmentIds })) }; },
  },
  watch: { value(value) { if (value) this.load(); } },
  methods: {
    async load() {
      this.loading = true; this.step = 1; this.preview = null; this.selectedStudentIds = [];
      this.effectiveDate = this.$moment().format("YYYY-MM-DD");
      try {
        const response = await customerService.getReEnrollmentOptions(this.customerId);
        this.students = response.data.students || []; this.departments = response.data.departments || [];
      } finally { this.loading = false; }
    },
    close() { if (!this.saving) this.open = false; },
    studentName(id) { const student = this.students.find(item => item.id === id); return student ? student.fullName : "Student"; },
    previousDepartmentNames(id) {
      const student = this.students.find(item => item.id === id); if (!student) return "";
      return (student.previousDepartmentIds || []).map(classId => { const item = this.departments.find(department => department.id === classId); return item && item.name; }).filter(Boolean).join(", ");
    },
    async next() {
      if (this.step === 1) {
        this.selections = this.selectedStudentIds.map(studentId => ({ studentId, departmentIds: [] })); this.step = 2; return;
      }
      this.previewing = true;
      try { const response = await customerService.previewReEnrollment(this.customerId, this.payload); this.preview = response.data; this.step = 3; }
      catch (error) { await this.$swal("Could not preview re-enrollment", (error.data && error.data.message) || "Please check the selections.", "error"); }
      finally { this.previewing = false; }
    },
    async submit() {
      this.saving = true;
      try {
        const response = await customerService.reEnroll(this.customerId, this.payload);
        const result = response.data;
        await this.$swal("Re-enrollment complete", result.invoiceLocalId ? `Invoice #${result.invoiceLocalId} was created for ${this.$options.filters.currency(result.total)} and is due in two days.` : "Students were enrolled in the normal billing cycle.", "success");
        this.open = false; this.$emit("completed", result);
      } catch (error) { await this.$swal("Re-enrollment failed", (error.data && error.data.message) || "No changes were saved.", "error"); }
      finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.re-enrollment-dialog__student { padding: 13px 8px; border-bottom: 1px solid #edf1ed; }
.re-enrollment-dialog__student small, .re-enrollment-dialog__assignment small, .re-enrollment-dialog__review small { display: block; color: #78847b; }
.re-enrollment-dialog__assignment { margin-bottom: 16px; padding: 16px; border: 1px solid #e2e9e3; border-radius: 12px; }
.re-enrollment-dialog__review { overflow: hidden; border: 1px solid #e2e9e3; border-radius: 12px; }
.re-enrollment-dialog__review > div { display: flex; justify-content: space-between; gap: 14px; padding: 13px 15px; border-bottom: 1px solid #edf1ed; }
.re-enrollment-dialog__review > div:last-child { border: 0; }
.re-enrollment-dialog__total { background: #f2f8f3; font-size: 16px; }
</style>
